import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BlogTemplate } from '../BlogTemplate';
import type { BlogPost } from '../../types/blog';

// Sample blog posts data
const sampleBlogPosts: BlogPost[] = [
  {
    id: '1',
    title: 'The Future of Artificial Intelligence in Healthcare: Transforming Patient Care Through Innovation',
    slug: 'future-ai-healthcare-patient-care',
    excerpt: 'Explore how AI is revolutionizing healthcare delivery, from diagnostic accuracy to personalized treatment plans, and what this means for the future of medicine.',
    content: `
      <h2 id="introduction">Introduction to AI in Healthcare</h2>
      <p>The healthcare industry stands at the precipice of a technological revolution. Artificial Intelligence (AI) is no longer a futuristic concept but a present reality that's transforming how we diagnose, treat, and prevent diseases. From machine learning algorithms that can detect cancer earlier than human radiologists to AI-powered drug discovery platforms that reduce development time from decades to years, the applications are both vast and profound.</p>
      
      <h2 id="current-applications">Current Applications and Breakthroughs</h2>
      <p>Today's AI healthcare applications span multiple domains, each addressing critical challenges in patient care and medical research.</p>
      
      <h3 id="diagnostic-accuracy">Enhanced Diagnostic Accuracy</h3>
      <p>AI systems are demonstrating remarkable capabilities in medical imaging and diagnostics. Deep learning models trained on millions of medical images can now identify conditions such as diabetic retinopathy, skin cancer, and pneumonia with accuracy rates that often exceed those of experienced physicians.</p>
      
      <blockquote>
        <p>"AI doesn't replace doctors; it amplifies their capabilities and allows them to focus on what they do best – caring for patients." - Dr. Sarah Chen, Chief Medical Officer, Stanford Health</p>
      </blockquote>
      
      <h3 id="personalized-medicine">Personalized Treatment Plans</h3>
      <p>The era of one-size-fits-all medicine is giving way to personalized treatment approaches powered by AI. By analyzing genetic data, lifestyle factors, and medical history, AI systems can recommend treatment protocols tailored to individual patients, improving outcomes and reducing adverse reactions.</p>
      
      <h2 id="challenges-opportunities">Challenges and Opportunities</h2>
      <p>While the potential of AI in healthcare is immense, several challenges must be addressed to realize its full potential.</p>
      
      <h3 id="data-privacy">Data Privacy and Security</h3>
      <p>Healthcare data is among the most sensitive information we generate. Implementing AI solutions requires robust security measures and compliance with regulations such as HIPAA, GDPR, and emerging AI governance frameworks.</p>
      
      <h3 id="integration-challenges">Integration with Existing Systems</h3>
      <p>Healthcare institutions often rely on legacy systems that weren't designed for AI integration. Successful implementation requires careful planning, significant investment, and change management strategies.</p>
      
      <h2 id="future-outlook">Future Outlook and Predictions</h2>
      <p>Looking ahead, we can expect to see even more transformative applications of AI in healthcare:</p>
      
      <ul>
        <li><strong>Predictive Analytics:</strong> AI systems that can predict health issues before symptoms appear</li>
        <li><strong>Robotic Surgery:</strong> Enhanced precision and reduced invasiveness in surgical procedures</li>
        <li><strong>Drug Discovery:</strong> Accelerated development of new treatments and therapies</li>
        <li><strong>Mental Health:</strong> AI-powered tools for early detection and intervention in mental health conditions</li>
      </ul>
      
      <h2 id="conclusion">Conclusion: A Collaborative Future</h2>
      <p>The future of healthcare lies not in replacing human expertise with artificial intelligence, but in creating a collaborative ecosystem where AI enhances human capabilities. As we continue to develop and refine these technologies, we must ensure they are implemented ethically, securely, and with the primary goal of improving patient outcomes and quality of life.</p>
      
      <p>The journey toward AI-powered healthcare is just beginning, and the possibilities are limitless. By embracing innovation while maintaining our focus on patient care, we can create a healthcare system that is more accurate, efficient, and accessible for all.</p>
    `,
    author: {
      name: 'Dr. Amira Hassan',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b47c?w=150&h=150&fit=crop&crop=face',
      bio: 'Dr. Hassan is a leading expert in healthcare AI with over 15 years of experience in medical technology and machine learning. She holds a PhD in Computer Science from MIT and an MD from Harvard Medical School.',
      id: 'author-1',
      title: 'Chief AI Researcher, amasQIS',
      social: {
        twitter: 'https://twitter.com/dramirahassan',
        linkedin: 'https://linkedin.com/in/amirahassan',
      }
    },
    publishedDate: '2024-01-15T10:00:00Z',
    readingTime: 8,
    tags: ['Artificial Intelligence', 'Healthcare', 'Machine Learning', 'Medical Technology', 'Innovation'],
    category: 'Healthcare AI',
    image: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
    likes: 245,
    comments: 18,
    views: 3420,
    featured: true,
    status: 'published'
  },
  {
    id: '2',
    title: 'Building Scalable IoT Solutions: From Edge Computing to Cloud Integration',
    slug: 'scalable-iot-solutions-edge-cloud',
    excerpt: 'Learn how to design and implement IoT architectures that scale from small deployments to enterprise-level solutions, covering edge computing, data processing, and cloud integration.',
    content: `
      <h2 id="iot-fundamentals">Understanding IoT Architecture Fundamentals</h2>
      <p>The Internet of Things (IoT) represents a paradigm shift in how we collect, process, and act upon data from the physical world. Building scalable IoT solutions requires a deep understanding of distributed systems, edge computing, and cloud architectures.</p>
      
      <h2 id="edge-computing">The Role of Edge Computing</h2>
      <p>Edge computing has emerged as a critical component in modern IoT architectures, bringing computation and data storage closer to the sources of data. This approach reduces latency, minimizes bandwidth usage, and enables real-time decision making.</p>
      
      <h3 id="edge-benefits">Benefits of Edge Processing</h3>
      <ul>
        <li>Reduced latency for time-critical applications</li>
        <li>Lower bandwidth costs through local data processing</li>
        <li>Improved reliability with offline capabilities</li>
        <li>Enhanced privacy and security through local data handling</li>
      </ul>
      
      <h2 id="cloud-integration">Cloud Integration Strategies</h2>
      <p>While edge computing handles immediate processing needs, cloud integration provides the scalability and advanced analytics capabilities necessary for comprehensive IoT solutions.</p>
      
      <pre><code>
// Example IoT data pipeline configuration
{
  "edgeProcessing": {
    "filters": ["temperature", "humidity", "motion"],
    "aggregation": "5min_average",
    "alertThresholds": {
      "temperature": { "min": 0, "max": 35 },
      "humidity": { "min": 30, "max": 70 }
    }
  },
  "cloudIntegration": {
    "batchSize": 1000,
    "uploadInterval": "15min",
    "retryPolicy": "exponential_backoff"
  }
}
      </code></pre>
      
      <h2 id="scaling-challenges">Scaling Challenges and Solutions</h2>
      <p>As IoT deployments grow from pilot projects to production systems, several scaling challenges emerge that require careful architectural consideration.</p>
      
      <h3 id="data-management">Data Management at Scale</h3>
      <p>Managing the volume, velocity, and variety of IoT data requires sophisticated data pipeline architectures that can handle millions of devices generating continuous streams of information.</p>
      
      <h2 id="implementation-best-practices">Implementation Best Practices</h2>
      <p>Successful IoT implementations follow established patterns and best practices that ensure reliability, security, and maintainability at scale.</p>
      
      <blockquote>
        <p>"The key to successful IoT scaling is to design for failure from day one. Every component should be replaceable, every connection should be redundant, and every process should be monitored."</p>
      </blockquote>
      
      <h2 id="future-trends">Future Trends in IoT Architecture</h2>
      <p>The IoT landscape continues to evolve rapidly, with emerging technologies like 5G, AI at the edge, and quantum computing promising to unlock new possibilities for connected device ecosystems.</p>
    `,
    author: {
      name: 'Mohamed Al-Rashid',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      bio: 'Mohamed specializes in large-scale IoT deployments and edge computing solutions. He has designed IoT systems for smart cities, industrial automation, and environmental monitoring.',
      id: 'author-2',
      title: 'Senior IoT Architect, amasQIS',
      social: {
        linkedin: 'https://linkedin.com/in/mohamedalrashid',
      }
    },
    publishedDate: '2024-01-12T14:30:00Z',
    readingTime: 12,
    tags: ['IoT', 'Edge Computing', 'Cloud Architecture', 'Scalability', 'System Design'],
    category: 'IoT & Infrastructure',
    image: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
    likes: 189,
    comments: 24,
    views: 2856,
    featured: false,
    status: 'published'
  },
  {
    id: '3',
    title: 'Machine Learning Model Deployment: From Development to Production',
    slug: 'ml-model-deployment-production',
    excerpt: 'A comprehensive guide to deploying machine learning models in production environments, covering containerization, monitoring, and MLOps best practices.',
    content: `
      <h2 id="deployment-overview">Deployment Challenges in Machine Learning</h2>
      <p>Moving machine learning models from development environments to production systems presents unique challenges that differ significantly from traditional software deployment. The complexity stems from the need to handle data dependencies, model versioning, and performance monitoring in dynamic environments.</p>
      
      <h2 id="containerization">Containerization and Orchestration</h2>
      <p>Docker and Kubernetes have become essential tools for ML model deployment, providing consistent environments and scalable orchestration capabilities.</p>
      
      <h3 id="docker-best-practices">Docker Best Practices for ML</h3>
      <p>When containerizing ML models, several considerations are crucial for optimal performance and maintainability.</p>
      
      <h2 id="monitoring-observability">Monitoring and Observability</h2>
      <p>Production ML systems require comprehensive monitoring that goes beyond traditional application metrics to include model performance, data drift, and prediction quality.</p>
      
      <h2 id="mlops-practices">MLOps Implementation</h2>
      <p>MLOps represents the convergence of machine learning, DevOps, and data engineering practices, enabling teams to deploy, monitor, and maintain ML models at scale.</p>
      
      <h2 id="conclusion">Building Robust ML Pipelines</h2>
      <p>Successful ML deployment requires a holistic approach that considers the entire model lifecycle, from initial training through production monitoring and eventual model retirement.</p>
    `,
    author: {
      name: 'Sarah Thompson',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
      bio: 'Sarah leads our MLOps initiatives and has extensive experience in deploying machine learning models at scale for enterprise clients.',
      id: 'author-3',
      title: 'MLOps Engineer, amasQIS',
    },
    publishedDate: '2024-01-10T09:15:00Z',
    readingTime: 10,
    tags: ['Machine Learning', 'MLOps', 'DevOps', 'Deployment', 'Monitoring'],
    category: 'AI & ML',
    image: 'https://images.unsplash.com/photo-1555949963-ff9fe719c7a1?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
    likes: 156,
    comments: 12,
    views: 2134,
    featured: false,
    status: 'published'
  }
];

export function BlogPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [currentPost, setCurrentPost] = useState<BlogPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);

  useEffect(() => {
    if (!slug) {
      navigate('/blog');
      return;
    }

    // Find the post by slug
    const post = sampleBlogPosts.find(p => p.slug === slug);
    
    if (!post) {
      navigate('/blog');
      return;
    }

    setCurrentPost(post);

    // Find related posts (same category or similar tags)
    const related = sampleBlogPosts
      .filter(p => p.id !== post.id)
      .filter(p => 
        p.category === post.category || 
        p.tags.some(tag => post.tags.includes(tag))
      )
      .sort((a, b) => {
        // Sort by category match first, then by shared tags
        const aScore = (a.category === post.category ? 2 : 0) + 
                      a.tags.filter(tag => post.tags.includes(tag)).length;
        const bScore = (b.category === post.category ? 2 : 0) + 
                      b.tags.filter(tag => post.tags.includes(tag)).length;
        return bScore - aScore;
      })
      .slice(0, 4);

    setRelatedPosts(related);
  }, [slug, navigate]);

  const handleLike = (postId: string) => {
    // In a real application, this would make an API call
    console.log(`Liked post: ${postId}`);
  };

  const handleShare = (postId: string, platform: string) => {
    // In a real application, this would track sharing analytics
    console.log(`Shared post ${postId} on ${platform}`);
  };

  if (!currentPost) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading article...</p>
        </div>
      </div>
    );
  }

  return (
    <BlogTemplate
      post={currentPost}
      relatedPosts={relatedPosts}
      onLike={handleLike}
      onShare={handleShare}
    />
  );
}